import { MyContract } from '@skippay/core/@types/myContract';
import { MyProfile } from '@skippay/core/@types/myProfile';
import { Maybe } from '@skippay/core/utils/types';

import { accountPackage, toIsoDate } from 'jarabot/utils';
import MyProfileRevolvingApplicationQuery from 'queries/MyProfileRevolvingApplicationQuery.graphql';

import { toActionFlags, toBlockedCategories } from '../../masking';
import JarabotComplianceQuery from '../../queries/JarabotComplianceQuery.graphql';
import { AccountInfo, Compliance, KycState } from '../../types';
import { DomainSource } from '../types';

type ProfileData = {
	myProfile?: {
		accountType?: MyProfile['accountType'];
		hasUsedPreferredBankAccountRecently?: MyProfile['hasUsedPreferredBankAccountRecently'];
		options?: MyProfile['options'];
	};
};

export const eligibilitySource: DomainSource<ProfileData> = {
	domains: ['eligibility', 'account'],
	query: MyProfileRevolvingApplicationQuery,
	toModel: (data) => {
		const profile = data.myProfile;
		const account: AccountInfo = {
			package: profile ? accountPackage(profile.accountType) : undefined,
			hasPreferredBank: profile?.hasUsedPreferredBankAccountRecently ?? undefined,
		};
		if (!profile) return { eligibility: undefined, account };
		return {
			eligibility: {
				canApplyForRevolving: profile.options?.canApplyForRevolvingContract ?? undefined,
				canApplyForInstallments: profile.options?.canApplyForInstallments ?? undefined,
				availableLoanTypes: profile.options?.availableInstallmentsLoanTypes ?? undefined,
			},
			account,
		};
	},
};

interface ComplianceData {
	myProfile?: {
		kyc?: Maybe<{
			isOutdated?: Maybe<boolean>;
			isDocumentExpired?: Maybe<boolean>;
			identityDocument?: Maybe<{ validity?: Maybe<string> }>;
		}>;
		amlLegalReview?: MyProfile['amlLegalReview'];
		amlKycRevalidation?: MyProfile['amlKycRevalidation'];
		pepRequirements?: MyProfile['pepRequirements'];
	} | null;
	myContract?: { customerBlockingReasons?: MyContract['customerBlockingReasons'] } | null;
}

const toKycState = (isOutdated?: boolean): KycState => {
	if (isOutdated === undefined) return KycState.UNKNOWN;
	return isOutdated ? KycState.OUTDATED : KycState.VERIFIED;
};

export const complianceSource: DomainSource<ComplianceData> = {
	domains: ['compliance'],
	query: JarabotComplianceQuery,
	toModel: (data) => {
		const profile = data.myProfile;
		if (!profile) return { compliance: undefined };
		const blockingReasons = data.myContract?.customerBlockingReasons ?? undefined;
		const compliance: Compliance = {
			actionsRequired: toActionFlags({
				kycIsOutdated: profile.kyc?.isOutdated ?? undefined,
				amlLegalReviewRequested: profile.amlLegalReview?.isRequested ?? undefined,
				amlLegalReviewOutdated: profile.amlLegalReview?.expiresAt
					? profile.amlLegalReview.expiresAt.slice(0, 10) < new Date().toISOString().slice(0, 10)
					: undefined,
				amlKycRevalidationState: profile.amlKycRevalidation?.state,
				pepAssetsOriginQuestionnaireRequired:
					profile.pepRequirements?.isAssetsOriginQuestionnaireRequired ?? undefined,
				pepBankIdRequired: profile.pepRequirements?.isBankIdRequired ?? undefined,
				customerBlockingReasons: blockingReasons,
			}),
			blockedCategories: toBlockedCategories(blockingReasons),
			kycState: toKycState(profile.kyc?.isOutdated ?? undefined),
			documentExpiry: toIsoDate(profile.kyc?.identityDocument?.validity),
			isDocumentExpired: profile.kyc?.isDocumentExpired ?? undefined,
			amlReviewExpiresAt: toIsoDate(profile.amlLegalReview?.expiresAt),
		};
		return { compliance };
	},
};
